import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, SvgIcon, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { toAbsoluteUrl } from "./link-url";

const COPIED_FEEDBACK_MS = 1500;

/**
 * Icon button that copies a link to the clipboard. Shows a "Link copied"
 * tooltip + check icon briefly after a successful copy.
 *
 * Clicks don't propagate, so it is safe inside clickable rows/cards. Hover
 * visibility is the parent's concern — pass it through `sx`.
 */
export function CopyLinkButton({
  url,
  size = "small",
  iconSize = 16,
  color = "primary",
  tooltipPlacement = "top",
  onCopy,
  sx,
}) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleClick = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!url) return;
    const absoluteUrl = toAbsoluteUrl(url);
    try {
      await navigator.clipboard.writeText(absoluteUrl);
    } catch {
      // Clipboard unavailable or denied (insecure context / permissions).
      toast.error(t("common.linkCopyFailed"));
      return;
    }
    setIsCopied(true);
    onCopy?.(absoluteUrl);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
  };

  if (!url) return null;

  return (
    <Tooltip
      title={isCopied ? t("common.linkCopied") : t("common.copyLink")}
      placement={tooltipPlacement}
    >
      <IconButton
        size={size}
        onClick={handleClick}
        aria-label={t("common.copyLink")}
        sx={sx}
      >
        <SvgIcon
          color={isCopied ? "success" : color}
          sx={{ fontSize: iconSize }}
        >
          <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
        </SvgIcon>
      </IconButton>
    </Tooltip>
  );
}

CopyLinkButton.propTypes = {
  url: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  iconSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  onCopy: PropTypes.func,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};
